
import { Add, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { CustomTextInput } from "components/atoms";
import React, { forwardRef, useRef, useState } from "react";
import { camelCaseToString, generateUUID, showToast } from "services/utils";
import { getRefValueByPath, setRefValueByPath, toCamelCase } from "../services";
import { cloneDeep } from "lodash";
import DialogBox from "./DialogBox";

const commonStyle = {
  tableCell: {
    textAlign: "center",
    minWidth: "150px",
    padding: "6px 12px",
    border: "1px solid #e0e0e0",
    fontWeight: 500,
  },
};

const createNewColumn = () => ({
  name: "",
  uniqueName: "",
  type: "text",
  isNew: true,
  uuid: generateUUID(),
});


const checkUniqueNamePresent = (name, columns = []) =>
  columns.some((col) => col.uniqueName === name);

const generateUniqueNameWithPrefix = (baseName, columns) => {
  let i = 0;
  let candidate = baseName;
  while (checkUniqueNamePresent(candidate, columns)) {
    candidate = `${baseName}${++i}`;
  }
  return candidate;
};


const handleChangeColumnName = (stateRef, render, column, columns, index, newValue, path = []) => {

  column.name = newValue

  if (!!column?.isNew) {
    let newUniqueName = toCamelCase(newValue)
    if (checkUniqueNamePresent(newUniqueName, columns)) {
      newUniqueName = generateUniqueNameWithPrefix(newUniqueName, columns)
    }
    column.uniqueName = newUniqueName
  }

  const key = 'uuid'

  const currentPath = path.slice(0, -1)
  currentPath.push(column?.uniqueName)

  const filterTrack = stateRef.current.trackChanges.filter((col) => (
    ((col?.[key] !== column?.[key]))
  ))

  filterTrack.push({ ...column, path: currentPath })
  setRefValueByPath(stateRef, "trackChanges", filterTrack)

  const deleteError = getRefValueByPath(stateRef, "errors").filter((uuid) => uuid !== column?.uuid)
  setRefValueByPath(stateRef, "errors", deleteError)
  render();
};

const handleDeleteColumn = (stateRef, render, index, path) => {
  const updated = [...(stateRef.current.columns ?? [])];

  const deleteByPath = (cols, [head, ...rest]) => {
    const idx = cols.findIndex(c => c.uniqueName === head);
    if (idx === -1) return;

    if (rest.length === 0) {
      cols.splice(idx, 1); 
    } else if (Array.isArray(cols[idx]?.value)) {
      deleteByPath(cols[idx].value, rest);
    }
  };

  if (Array.isArray(path) && path.length) {
    deleteByPath(updated, path);
  } else if (typeof index === "number") {
    updated.splice(index, 1);
  }
  stateRef.current.columns = updated;
  render();
};


const handleAddColumn = (stateRef, render, columns, index) => {
  columns.splice(index + 1, 0, createNewColumn());
  render();
};

const StructureCell = ({ columns, col, colIndex, stateRef, render, path }) => {

  const error = getRefValueByPath(stateRef, "errors").some((id) => id === col?.uuid)

  return (
    <CustomTextInput
      value={col?.name}
      onChange={(e) =>
        handleChangeColumnName(stateRef, render, col, columns, colIndex, e.target.value, path)
      }
      autoFocus={col?.isNew}
      error={error}
      helperText={error ? "this field required..!" : ""}
    />
  );
};

const GenerateNestedColumns = (stateRef, render, columns, nested, config = {}, path = []) => {
  const viewMode = config?.isViewMode
  return columns?.map((col, colIndex) => {
  if (col?.type === "nested") {
    return (
      <TableCell
        key={col.uniqueName || colIndex}
        sx={{
          ...commonStyle.tableCell,
          padding: 0,
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
            background: viewMode ? "#fafafa" : "inherit",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={(col?.value?.length || 0) + 1}
                sx={{
                  ...commonStyle.tableCell,
                  borderBottom: "1px solid #bdbdbd",
                  background: "#f5f5f5",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  textAlign: "center",
                  fontSize: "0.9rem",
                }}
              >
                {camelCaseToString(col?.uniqueName)}
              </TableCell>
            </TableRow>
            <TableRow>
              {GenerateNestedColumns(
                stateRef,
                render,
                col?.value ?? [],
                true,
                config,
                [...path, col?.uniqueName],
                viewMode // pass down
              )}
            </TableRow>
          </TableHead>
        </Table>
      </TableCell>
    );
  } else {
    return (
      <React.Fragment key={col.uniqueName || colIndex}>
        <TableCell
          sx={{
            ...commonStyle.tableCell,
            "&:first-of-type": { borderLeft: "1px solid #e0e0e0" },
            "&:last-of-type": { borderRight: "1px solid #e0e0e0" },
          }}
        >
          {viewMode ? (
            <Box
              sx={{
                width: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
                py: 0.5,
                color: "#424242",
                borderRadius: "6px",
                background: "#fafafa",
              }}
            >
              {col?.name ?? "-"}
            </Box>
          ) : (
            <Box
              sx={{
                width: "300px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <StructureCell
                col={col}
                colIndex={colIndex}
                stateRef={stateRef}
                render={render}
                columns={columns}
                path={[...path, col?.uniqueName]}
              />

              {!!config?.onDelete !== false &&
                !!config?.onDelete?.isVisible && (
                  <IconButton
                    color="error"
                    onClick={() =>
                      handleDeleteColumn(stateRef, render, colIndex, [
                        ...path,
                        col?.uniqueName,
                      ])
                    }
                    disabled={
                      nested
                        ? columns?.length < 2
                        : columns?.length < 3 && !!config?.onDelete?.isActive
                    }
                  >
                    <Delete />
                  </IconButton>
                )}
            </Box>
          )}
        </TableCell>

        {!viewMode &&
          !!config?.addRow !== false &&
          !!config?.addRow?.isVisible &&
          ((colIndex === columns.length - 1) ||
            columns[colIndex + 1]?.type === "nested") && (
            <TableCell sx={{ ...commonStyle.tableCell }}>
              <IconButton
                color="success"
                onClick={() =>
                  handleAddColumn(stateRef, render, columns, colIndex, [
                    ...path,
                    col?.uniqueName,
                  ])
                }
                disabled={!config?.addRow?.isActive}
              >
                <Add />
              </IconButton>
            </TableCell>
          )}
      </React.Fragment>
    );
  }
});
};

const addUUID = (columns) => {
  columns?.forEach((col) => {
    if (col?.type === 'nested') {
      addUUID(col?.value)
    } else {
      col.uuid = generateUUID()
    }
  })

  return columns
}

const StructureNested = (props) => {
  const { columns = [], config = {} } = props

  const {
  linkingData = [],
  onSubmit = () => {},
  wrapperSupport = "off",
  wrapperConfig = {},     
} = config || {};        

const { syncing = {} } = wrapperConfig;     

  const { isSubmitVisible = true } = onSubmit || {}

  const [, forceUpdate] = useState({});
  const render = () => forceUpdate({});

  const stateRef = useRef({
    modelOpen: false,
    columns: addUUID(cloneDeep(columns)) ?? [],
    trackChanges: [],
    errors: []
  });

  const checkValidation = (columns, error, meta = { validate: true }, path = []) => {
    columns.forEach((col) => {
      if (col?.type === 'nested') {
        checkValidation(col?.value, error, meta, [...path, col?.uniqueName])
      } else {
        if (!col?.name || !col?.uniqueName) {
          error.push(col?.uuid)
          meta.validate = false
        }
      }
    })

    return meta
  }

  const finalSubmit = () => {
    setRefValueByPath(stateRef, "modelOpen", false)

    const finalColumns = getRefValueByPath(stateRef, "columns").map((col) => {
      const { isNew, uuid, ...other } = col
      return other
    });

    if (!(wrapperSupport === "on" && syncing?.submits)) {
      if (!!onSubmit) {
        !!handler && typeof handler === "function" && handler(finalColumns);
      }
      getRefValueByPath(stateRef, "modelOpen")
        ? setRefValueByPath(stateRef, "modelOpen", false)
        : null;
    }

    !!onSubmit.handler && (typeof onSubmit.handler === "function") && ( onSubmit.handler(finalColumns) )

    render()
  }

  const handleSave = () => {
    const isValidate = checkValidation(stateRef?.current?.columns, stateRef.current.errors)

    if (!isValidate.validate) {
      showToast("Error in table...!", false)
      render()
    }

    const hasLinkColChage = getRefValueByPath(stateRef, "trackChanges").filter((col) => !!col.linkedKey)

    if (wrapperSupport === "on" && syncing?.submits) {
      return {
        hasLinkColChage,
        columns: getRefValueByPath(stateRef, "columns"),
        trackChanges: getRefValueByPath(stateRef, "trackChanges"),
        tableType: config?.type,
      };
    }

    if (hasLinkColChage.length > 0) {
      setRefValueByPath(stateRef, "modelOpen", true)
      render()
      return
    } else {
      finalSubmit()
    }
    
  }

  !!config && (config.trackChanges = getRefValueByPath(stateRef, "trackChanges"))
  !!config && (config.SUBMIT_HANDLER = handleSave)

  return (
    <>

      <DialogBox
        state={getRefValueByPath(stateRef, "modelOpen")}
        hanldeToggle={() => {
          setRefValueByPath(
            stateRef,
            "modelOpen",
            !getRefValueByPath(stateRef, "modelOpen")
          );
          render();
        }}
        hanldeNo={() => {
          setRefValueByPath(stateRef, "modelOpen", false);
          render();
        }}
        handleYes={() => {
          finalSubmit()
        }}
        data={linkingData}
      />


      <TableContainer component={Paper} sx={{ paddingY: "10px" }}>
        <Table sx={{ borderCollapse: "collapse", width: "100%" }}>
          <TableHead>
            <TableRow>
              {GenerateNestedColumns(
                stateRef,
                render,
                stateRef.current.columns,
                false,
                config,
              )}
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
          mt: '20px'
        }}
      >
        {
          (!config?.isViewMode && isSubmitVisible && !((wrapperSupport === "on") && syncing?.submits)) && (
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          )
        }
      </Box>
    </>
  );
};

const WrappedStructureNested = forwardRef(StructureNested);
WrappedStructureNested.__IS_DYNAMIC_TABLE__ = true;

export default WrappedStructureNested;
